import { onBackendAvailable } from '@affine/core/utils/backend-availability';
import { DebugLogger } from '@affine/debug';
import { UserFriendlyError } from '@affine/error';
import type { OAuthProviderType } from '@affine/graphql';
import { track } from '@affine/track';
import {
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import {
  LOCAL_DATA_ACCESS_ERROR_CODES,
  localAccountKeyError,
} from '@streamlain/view-config/local-data-seal';
import { OnEvent, Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { distinctUntilChanged, map, skip } from 'rxjs';

import { ApplicationFocused } from '../../lifecycle';
import { LocalAccountKeys } from '../../local-data-keys/local-account-keys';
import type { UrlService } from '../../url';
import { AuthSession } from '../entities/session';
import { AccountChanged } from '../events/account-changed';
import { AccountLoggedIn } from '../events/account-logged-in';
import { AccountLoggedOut } from '../events/account-logged-out';
import { ServerStarted } from '../events/server-started';
import type {
  LocalDataLoginResult,
  SignInPasswordResult,
} from '../provider/auth';
import type { AuthStore } from '../stores/auth';
import type { FetchService } from './fetch';
import { ServerService } from './server';

export type AccessGateMode = 'open' | 'gated' | null;
export type AccessGateChannel = 'magic-link' | 'oauth' | 'invite-password';

const ADMISSION_EXPIRY_SKEW_MS = 5_000;
const localKeyLogger = new DebugLogger('affine:local-data-keys');

export function consumeAccessCodeFragment(href: string): {
  code?: string;
  funnelId?: string;
  sanitizedPath?: string;
} {
  const url = new URL(href);
  const fragment = new URLSearchParams(url.hash.slice(1));
  const rawCode = fragment.get('access-code');
  const rawFunnelId = fragment.get('funnel-id');
  if (rawCode === null && rawFunnelId === null) return {};

  fragment.delete('access-code');
  fragment.delete('funnel-id');
  const remainingFragment = fragment.toString();
  const normalizedCode = rawCode?.trim();
  const code =
    normalizedCode && normalizedCode.length <= 32 ? normalizedCode : undefined;
  return {
    code,
    funnelId:
      code &&
      rawFunnelId &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        rawFunnelId
      )
        ? rawFunnelId.toLowerCase()
        : undefined,
    sanitizedPath: `${url.pathname}${url.search}${remainingFragment ? `#${remainingFragment}` : ''}`,
  };
}

@OnEvent(ApplicationFocused, e => e.onApplicationFocused)
@OnEvent(ServerStarted, e => e.onServerStarted)
export class AuthService extends Service {
  session = this.framework.createEntity(AuthSession);
  private readonly unsubscribeBackendAvailability: () => void;
  private lastAccountId: string | null = null;
  private accessCode?: string;
  private funnelId?: string;
  private readonly admissions = new Map<
    string,
    { admissionId: string; expiresAt: number }
  >();

  constructor(
    private readonly fetchService: FetchService,
    private readonly store: AuthStore,
    private readonly urlService: UrlService
  ) {
    super();

    this.lastAccountId = this.session.account$.value?.id ?? null;
    this.session.account$
      .pipe(
        map(a => ({
          id: a?.id,
          account: a,
        })),
        distinctUntilChanged((a, b) => a.id === b.id), // only emit when the value changes
        skip(1) // skip the initial value
      )
      .subscribe(({ account }) => {
        const previousAccountId = this.lastAccountId;
        this.lastAccountId = account?.id ?? null;
        if (previousAccountId && previousAccountId !== this.lastAccountId) {
          // The server no longer authenticates the previous account here.
          this.framework
            .get(LocalAccountKeys)
            .beginLogout(
              this.framework.get(ServerService).server.id,
              previousAccountId
            )
            .catch(error =>
              this.logLocalKeyFailure('beginLogout failed', error)
            );
        }
        if (account === null) {
          this.eventBus.emit(AccountLoggedOut, account);
        } else {
          this.eventBus.emit(AccountLoggedIn, account);
        }
        this.eventBus.emit(AccountChanged, account);
      });

    this.unsubscribeBackendAvailability = onBackendAvailable(() => {
      this.session.revalidate();
    });
  }

  get cachedAccountId(): string | null {
    return this.store.getCachedAuthSession()?.account.id ?? null;
  }

  get confirmedAccountId(): string | null {
    const confirmedAccountId = this.session.confirmedAccountId$.value;
    return confirmedAccountId === this.cachedAccountId
      ? confirmedAccountId
      : null;
  }

  get confirmedAccountId$() {
    return this.session.confirmedAccountId$;
  }

  private onServerStarted() {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const fragment = new URLSearchParams(url.hash.slice(1));
      if (fragment.has('local-key-exchange')) {
        const codes = fragment.getAll('local-key-exchange');
        fragment.delete('local-key-exchange');
        const remaining = fragment.toString();
        window.history.replaceState(
          window.history.state,
          '',
          `${url.pathname}${url.search}${remaining ? '#' + remaining : ''}`
        );
        if (codes.length === 1 && /^[A-Za-z0-9_-]{43}$/.test(codes[0])) {
          void this.completeSamlLogin(codes[0]);
        } else {
          this.logLocalKeyFailure('login-exchange failed', {
            code: 'KEY_UNAVAILABLE',
          });
          this.session.revalidate();
        }
        return;
      }
    }
    this.session.revalidate();
  }

  private async completeSamlLogin(code: string): Promise<void> {
    try {
      const response = await this.fetchService.fetch(
        '/api/auth/local-data-key/login-exchange',
        {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          referrerPolicy: 'no-referrer',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        }
      );
      if (!response.ok) throw localAccountKeyError('KEY_UNAVAILABLE');
      await this.completeLocalDataLogin(await response.json());
    } catch (error) {
      this.logLocalKeyFailure('login-exchange failed', error);
    } finally {
      this.session.revalidate();
    }
  }

  private async completeLocalDataLogin(
    result: LocalDataLoginResult
  ): Promise<void> {
    if (
      typeof result?.localDataLoginProof !== 'string' ||
      !result.localDataLoginProof ||
      typeof result.localDataAccountId !== 'string' ||
      !result.localDataAccountId
    ) {
      this.logLocalKeyFailure('completeLogin failed', {
        code: 'KEY_UNAVAILABLE',
      });
      return;
    }
    try {
      const flavour = this.framework.get(ServerService).server.id;
      await this.framework
        .get(LocalAccountKeys)
        .completeLogin(
          flavour,
          result.localDataAccountId,
          result.localDataLoginProof
        );
    } catch (error) {
      this.logLocalKeyFailure('completeLogin failed', error);
    }
  }

  private logLocalKeyFailure(label: string, error: unknown): void {
    const rawCode =
      error && typeof error === 'object' && 'code' in error
        ? error.code
        : undefined;
    const code =
      LOCAL_DATA_ACCESS_ERROR_CODES.find(code => code === rawCode) ?? 'UNKNOWN';
    localKeyLogger.error(label, { code });
  }

  private onApplicationFocused() {
    this.session.revalidate();
  }

  captureAccessCodeFromLocation(): void {
    if (typeof window === 'undefined') return;
    const result = consumeAccessCodeFragment(window.location.href);
    if (!result.sanitizedPath) return;
    this.accessCode = result.code;
    this.funnelId = result.funnelId;
    window.history.replaceState(window.history.state, '', result.sanitizedPath);
  }

  setAccessCode(code: string): void {
    const normalized = code.trim();
    this.accessCode =
      normalized.length > 0 && normalized.length <= 32 ? normalized : undefined;
    this.admissions.clear();
    this.funnelId = undefined;
  }

  hasAccessCode(): boolean {
    return this.accessCode !== undefined;
  }

  async getAccessMode(): Promise<AccessGateMode> {
    const response = await this.fetchService.fetch('/api/public/access/mode', {
      cache: 'no-store',
    });
    const data = (await response.json()) as { mode?: unknown };
    if (data.mode !== null && data.mode !== 'open' && data.mode !== 'gated') {
      throw new Error('Invalid access mode response');
    }
    return data.mode;
  }

  async createAccessAdmission(
    email: string,
    channel: AccessGateChannel
  ): Promise<string | undefined> {
    if (!this.accessCode) {
      return undefined;
    }
    const mode = await this.getAccessMode();
    if (mode === 'open') return undefined;
    if (mode === null) throw new Error('Registration is unavailable');
    const normalizedEmail = email.trim().toLowerCase();
    const cacheKey = `${channel}:${normalizedEmail}`;
    const cached = this.admissions.get(cacheKey);
    if (cached && cached.expiresAt - ADMISSION_EXPIRY_SKEW_MS > Date.now()) {
      return cached.admissionId;
    }
    this.admissions.delete(cacheKey);

    const response = await this.fetchService.fetch(
      '/api/public/access/admissions',
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          code: this.accessCode,
          email: normalizedEmail,
          channel,
          ...(this.funnelId ? { funnelId: this.funnelId } : {}),
        }),
      }
    );
    const data = (await response.json()) as {
      admissionId?: unknown;
      expiresAt?: unknown;
    };
    const expiresAt =
      typeof data.expiresAt === 'string' ? Date.parse(data.expiresAt) : NaN;
    if (
      typeof data.admissionId !== 'string' ||
      data.admissionId.length === 0 ||
      !Number.isFinite(expiresAt) ||
      expiresAt <= Date.now()
    ) {
      throw new Error('Invalid access admission response');
    }
    this.admissions.set(cacheKey, { admissionId: data.admissionId, expiresAt });
    return data.admissionId;
  }

  private invalidateAccessAdmission(admissionId?: string): void {
    if (!admissionId) return;
    for (const [key, cached] of this.admissions) {
      if (cached.admissionId === admissionId) this.admissions.delete(key);
    }
  }

  async sendEmailMagicLink(
    email: string,
    verifyToken?: string,
    challenge?: string,
    redirectUrl?: string, // url to redirect to after signed-in
    intent?: 'default' | 'invite' | 'registration',
    admissionId?: string
  ) {
    track.$.$.auth.signIn({ method: 'magic-link' });
    this.setClientNonce();
    try {
      const scheme = this.urlService.getClientScheme();
      const magicLinkUrlParams = new URLSearchParams();
      magicLinkUrlParams.set('email', email);
      if (redirectUrl) {
        magicLinkUrlParams.set('redirect_uri', redirectUrl);
      }
      if (intent && intent !== 'default') {
        magicLinkUrlParams.set('intent', intent);
      }
      if (scheme) {
        magicLinkUrlParams.set('client', scheme);
      }
      await this.fetchService.fetch('/api/auth/sign-in', {
        method: 'POST',
        body: JSON.stringify({
          email,
          // we call it [callbackUrl] instead of [redirect_uri]
          // to make it clear the url is used to finish the sign-in process instead of redirect after signed-in
          callbackUrl: `/magic-link?${magicLinkUrlParams.toString()}`,
          client_nonce: this.store.getClientNonce(),
          admissionId,
        }),
        headers: {
          'content-type': 'application/json',
          ...(verifyToken ? this.captchaHeaders(verifyToken, challenge) : {}),
        },
      });
    } catch (e) {
      this.invalidateAccessAdmission(admissionId);
      track.$.$.auth.signInFail({
        method: 'magic-link',
        reason: UserFriendlyError.fromAny(e).name,
      });
      throw e;
    }
  }

  async requestPasswordRecovery(email: string, verifyToken: string) {
    await this.fetchService.fetch('/api/auth/password-recovery/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
      headers: {
        'content-type': 'application/json',
        'x-altcha-payload': verifyToken,
      },
    });
  }

  async completePasswordRecovery(
    token: string,
    userId: string,
    newPassword: string
  ): Promise<
    LocalDataLoginResult & {
      email: string;
      requiresTwoFactor: boolean;
      preferredMethod?: 'totp' | 'email';
    }
  > {
    const response = await this.fetchService.fetch(
      '/api/auth/password-recovery/complete',
      {
        method: 'POST',
        body: JSON.stringify({ token, userId, newPassword }),
        headers: { 'content-type': 'application/json' },
      }
    );
    const result = (await response.json()) as LocalDataLoginResult & {
      email: string;
      requiresTwoFactor: boolean;
      preferredMethod?: 'totp' | 'email';
    };
    if (!result.requiresTwoFactor) {
      await this.completeLocalDataLogin(result);
      await this.session.waitForRevalidation();
    }
    return result;
  }

  async signInMagicLink(email: string, token: string, byLink = true) {
    const method = byLink ? 'magic-link' : 'otp';
    try {
      const result = await this.store.signInMagicLink(email, token);

      if (result.requiresTwoFactor) {
        this.store.setCachedAuthSession(null);
        return result;
      }

      await this.completeLocalDataLogin(result);
      await this.session.waitForRevalidation();
      track.$.$.auth.signedIn({ method });
      return result;
    } catch (e) {
      track.$.$.auth.signInFail({
        method,
        reason: UserFriendlyError.fromAny(e).name,
      });
      throw e;
    }
  }

  async oauthPreflight(
    provider: OAuthProviderType,
    client: string,
    postAuthRedirectUrl?: string,
    registrationTermsAccepted?: boolean,
    admissionId?: string
  ): Promise<Record<string, string>> {
    this.setClientNonce();
    try {
      const res = await this.fetchService.fetch('/api/oauth/preflight', {
        method: 'POST',
        body: JSON.stringify({
          provider,
          client,
          post_auth_redirect_uri: postAuthRedirectUrl,
          registration_terms_accepted: registrationTermsAccepted === true,
          client_nonce: this.store.getClientNonce(),
          admissionId,
        }),
        headers: {
          'content-type': 'application/json',
        },
      });

      return await res.json();
    } catch (e) {
      this.invalidateAccessAdmission(admissionId);
      track.$.$.auth.signInFail({
        method: 'oauth',
        provider,
        reason: UserFriendlyError.fromAny(e).name,
      });
      throw e;
    }
  }

  async signInOauth(code: string, state: string, provider: string) {
    try {
      const result = await this.store.signInOauth(code, state, provider);

      if (result.requiresTwoFactor) {
        this.store.setCachedAuthSession(null);
        return result;
      }

      await this.completeLocalDataLogin(result);
      await this.session.waitForRevalidation();
      if (this.session.status$.value !== 'authenticated') {
        await new Promise(resolve => setTimeout(resolve, 150));
        await this.session.waitForRevalidation();
      }
      if (this.session.status$.value !== 'authenticated') {
        throw new Error(
          'OAuth sign-in did not create an authenticated session'
        );
      }

      track.$.$.auth.signedIn({ method: 'oauth', provider });
      return result;
    } catch (e) {
      track.$.$.auth.signInFail({
        method: 'oauth',
        provider,
        reason: UserFriendlyError.fromAny(e).name,
      });
      throw e;
    }
  }

  async signInPassword(credential: {
    email: string;
    password: string;
    verifyToken?: string;
    challenge?: string;
  }): Promise<SignInPasswordResult> {
    track.$.$.auth.signIn({ method: 'password' });
    try {
      const result = await this.store.signInPassword(credential);

      // sl-qsjm.4: If 2FA is required, don't revalidate session yet.
      // The session exists but is unverified — revalidating would show
      // the user as authenticated before completing 2FA.
      if (result.requiresTwoFactor) {
        this.store.setCachedAuthSession(null);
        return result;
      }

      await this.completeLocalDataLogin(result);
      this.session.revalidate();
      track.$.$.auth.signedIn({ method: 'password' });
      return result;
    } catch (e) {
      track.$.$.auth.signInFail({
        method: 'password',
        reason: UserFriendlyError.fromAny(e).name,
      });
      throw e;
    }
  }

  /**
   * sl-qsjm.4: Complete 2FA verification and finalize login.
   * Called after TOTP/Email-OTP/backup code verification succeeds.
   */
  async completeTwoFactorLogin(result: LocalDataLoginResult) {
    await this.completeLocalDataLogin(result);
    this.session.revalidate();
    track.$.$.auth.signedIn({ method: 'password' });
  }

  async completeReactivationLogin(result: LocalDataLoginResult) {
    await this.completeLocalDataLogin(result);
    this.session.revalidate();
  }

  async signOut() {
    // Local account authority is locked durably before the server forgets the session.
    const accountId = this.session.account$.value?.id;
    if (accountId)
      await this.framework
        .get(LocalAccountKeys)
        .beginLogout(this.framework.get(ServerService).server.id, accountId);
    await this.store.signOut();
    this.store.setCachedAuthSession(null);
    this.session.revalidate();
  }

  // ===========================================================================
  // sl-wq59.6.4: WebAuthn / FIDO2 Passkey
  // ===========================================================================

  /**
   * Feature-detect WebAuthn. Components hide passkey UI when this returns
   * false. We treat the platform-authenticator check as a separate, optional
   * hint — the primary gate is just "does this browser speak WebAuthn".
   */
  static isWebAuthnSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.PublicKeyCredential !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      !!navigator.credentials
    );
  }

  /**
   * Enrol a new passkey against the current session. Triggers the browser
   * authenticator UI (Face ID / Touch ID / Windows Hello / security key).
   * Throws if the user cancels or the authenticator rejects.
   */
  async registerPasskey(deviceName?: string) {
    track.$.$.auth.enrollPasskey({});
    try {
      const { options, challengeRef } =
        await this.store.webauthnRegisterOptions();
      const response = await startRegistration({ optionsJSON: options });
      const result = await this.store.webauthnRegisterVerify(
        challengeRef,
        response,
        deviceName
      );
      track.$.$.auth.enrolledPasskey({});
      return result;
    } catch (e) {
      track.$.$.auth.enrollPasskeyFail({
        reason: UserFriendlyError.fromAny(e).name,
      });
      throw e;
    }
  }

  /**
   * Session-less passkey login (usernameless resident-key flow). On success
   * the server sets the session cookie; we await session revalidation so
   * callers observe authenticated state before their success side-effects
   * (e.g. redirect) fire — otherwise onAuthenticated would race the fetch.
   */
  async signInPasskey() {
    track.$.$.auth.signIn({ method: 'passkey' });
    try {
      const { options, challengeRef } = await this.store.webauthnLoginOptions();
      const response = await startAuthentication({ optionsJSON: options });
      const result = await this.store.webauthnLoginVerify(
        challengeRef,
        response
      );
      await this.completeLocalDataLogin(result);
      await this.session.waitForRevalidation();
      track.$.$.auth.signedIn({ method: 'passkey' });
      return result;
    } catch (e) {
      track.$.$.auth.signInFail({
        method: 'passkey',
        reason: UserFriendlyError.fromAny(e).name,
      });
      throw e;
    }
  }

  async listPasskeys() {
    return this.store.webauthnListCredentials();
  }

  async removePasskey(credentialId: string) {
    await this.store.webauthnRemoveCredential(credentialId);
  }

  // sl-zvjz: Account-Deletion lifecycle.
  //
  // Replaces the previous hard-delete `deleteAccount()` flow. Schedule starts
  // a 30-day grace period; the user is logged out everywhere because the
  // backend nukes their sessions. Reactivation is possible via cancel until
  // the grace window closes.

  async getAccountDeletionPreview() {
    return this.store.getAccountDeletionPreview();
  }

  async getAccountDeletionStatus() {
    return this.store.getAccountDeletionStatus();
  }

  async scheduleAccountDeletion(reason?: string) {
    const res = await this.store.scheduleAccountDeletion(reason);
    // Backend invalidated all sessions — clear local cache and revalidate so
    // the UI redirects to /sign-in on next render.
    this.store.setCachedAuthSession(null);
    this.session.revalidate();
    return res;
  }

  async cancelAccountDeletion() {
    return this.store.cancelAccountDeletion();
  }

  captchaHeaders(token: string, _challenge?: string) {
    // sl-wwta.3: ALTCHA PoW payload — backend reads x-altcha-payload
    return { 'x-altcha-payload': token } as Record<string, string>;
  }

  private setClientNonce() {
    if (BUILD_CONFIG.isNative) {
      // send random client nonce on native app
      this.store.setClientNonce(nanoid());
    }
  }

  override dispose(): void {
    this.unsubscribeBackendAvailability();
    super.dispose();
  }
}
